import { useState, useMemo } from 'react';
import {
  SpaceBetween,
  FormField,
  Autosuggest,
  Select,
  Multiselect,
  Input,
  Container,
  Header,
  SelectProps,
  MultiselectProps,
  Table,
  Badge,
  Button,
  Box,
  Modal,
} from '@amzn/awsui-components-react';
import { OptionDefinition } from '@amzn/awsui-components-react/polaris/internal/components/option/interfaces';
import { EventStatus, ExternalPartner, Contact } from '@amzn/red-velvet-api';
import { OptionalLabel, EventStepProps } from './Common';
import { PARTNER_OPTIONS, MAX_AMAZON_CONTACTS, MAX_COORDINATORS } from '../constants';
import { usePapiSuggestions } from '../../../../hooks/usePapiSuggestions';
import { useResolveAliasNames } from '../../../../hooks/useResolveAliasNames';
import { publishKatalMetric } from '../../../katalAnalytics';
import { FIELD_LIMITS } from '../../../../utils/fieldConstraints';
import { useUser } from '../../../../hooks/user';
import { getAliasFromEmail } from '../../../../utils/contactUtils';
import { useLocalizedText } from '../../../../hooks/localizedText';
import { useTOptions } from '../../../../hooks/localization/tOptions';
import { ERG_SPONSORS } from '../../../../data/eventOptionLists';

interface CoordinatorRow {
  alias: string;
  name: string;
  role: 'owner' | 'coordinator';
}

export interface CoordinatorsPartnersProps extends EventStepProps {
  ownerAlias?: string;
  eventStatus?: EventStatus;
  onCoordinatorNotificationInfoClick?: () => void;
}

/** Pre-fills a contact entry so the coordinator appears in the contacts list immediately. */
function buildMirroredContact(
  alias: string,
  name: string | undefined,
  email: string | undefined,
  existingContacts: Contact[]
): Contact | null {
  if (!email) return null;
  if (existingContacts.some((c) => c.alias === alias)) return null;
  if (existingContacts.length >= MAX_AMAZON_CONTACTS) return null;
  return { name: name ?? '', email, phone: '', alias };
}

export function CoordinatorsPartnersComponent({
  currentFields,
  updateFieldHandler,
  errors,
  isReadOnly,
  metricPrefix,
  ownerAlias: ownerAliasProp,
  eventStatus,
}: CoordinatorsPartnersProps) {
  const namespaces = ['events', 'translation'];
  const { t } = useLocalizedText(namespaces);
  const { userId } = useUser();

  const initialOwnerAlias = ownerAliasProp ?? userId ?? '';
  const ownerAlias = currentFields.ownerAlias ?? initialOwnerAlias;
  const isCurrentUserOwner = !!ownerAlias && userId === ownerAlias;
  const isDraft = !eventStatus || eventStatus === EventStatus.DRAFT;
  // Coordinators can add/remove peers; only the owner can transfer ownership
  // (or anyone, while drafting).
  const canEdit = !isReadOnly;
  const canTransferOwnership = !isReadOnly && (isDraft || isCurrentUserOwner);

  const partnerOptions: SelectProps.Options = PARTNER_OPTIONS.map((opt) => ({
    label: t(opt.labelKey),
    value: opt.value,
  }));
  const elgOptions: SelectProps.Options = useTOptions(namespaces, ERG_SPONSORS);

  const [coordinatorSearch, setCoordinatorSearch] = useState('');
  const [otherPartnerDraft, setOtherPartnerDraft] = useState<string | null>(null);
  const [editingRole, setEditingRole] = useState<string | null>(null);
  const [transferTarget, setTransferTarget] = useState<string | null>(null);

  const additionalCoordinators = useMemo(
    () => currentFields.additionalCoordinators ?? [],
    [currentFields.additionalCoordinators]
  );

  const aliasesToResolve = useMemo(
    () => [ownerAlias, ...additionalCoordinators].filter(Boolean),
    [ownerAlias, additionalCoordinators]
  );
  const nameCache = useResolveAliasNames(aliasesToResolve);

  const papiSearch = usePapiSuggestions();
  const coordinatorSuggestions = useMemo(() => {
    const filterOut = new Set(additionalCoordinators);
    filterOut.add(ownerAlias);
    return papiSearch.suggestions.filter((s) => !filterOut.has(s.value));
  }, [papiSearch.suggestions, ownerAlias, additionalCoordinators]);

  const otherPartnerValue = otherPartnerDraft ?? currentFields.otherPartner ?? '';

  const selectedPartner: OptionDefinition | null = currentFields.externalPlanningPartner
    ? ((partnerOptions.find(
        (opt) => (opt as OptionDefinition).value === currentFields.externalPlanningPartner
      ) as OptionDefinition) ?? null)
    : null;

  const selectedElgs: MultiselectProps.Options = (currentFields.elgAffiliations ?? [])
    .map(
      (val) => elgOptions.find((opt) => (opt as OptionDefinition).value === val) as OptionDefinition
    )
    .filter(Boolean);

  const rows: CoordinatorRow[] = [
    { alias: ownerAlias, name: nameCache[ownerAlias] || '', role: 'owner' },
    ...additionalCoordinators.map((alias) => ({
      alias,
      name: nameCache[alias] || '',
      role: 'coordinator' as const,
    })),
  ];

  const handleAddCoordinator = (alias: string) => {
    const trimmed = alias.trim();
    if (!trimmed || trimmed === ownerAlias) return;
    if (additionalCoordinators.includes(trimmed)) return;
    if (additionalCoordinators.length >= MAX_COORDINATORS) return;

    const name = papiSearch.getNameForAlias(trimmed);
    const email = papiSearch.getEmailForAlias(trimmed);
    const existingContacts = currentFields.amazonContacts ?? [];
    const mirroredContact = buildMirroredContact(trimmed, name, email, existingContacts);

    publishKatalMetric(`${metricPrefix}_coordinatorAdded`, '');
    updateFieldHandler({
      ...currentFields,
      additionalCoordinators: [...additionalCoordinators, trimmed],
      ...(mirroredContact && { amazonContacts: [...existingContacts, mirroredContact] }),
    });
    setCoordinatorSearch('');
  };

  const handleRemoveCoordinator = (alias: string) => {
    if (alias === ownerAlias) return;
    if (editingRole === alias) setEditingRole(null);
    publishKatalMetric(`${metricPrefix}_coordinatorRemoved`, '');
    const updated = additionalCoordinators.filter((a) => a !== alias);
    updateFieldHandler({ ...currentFields, additionalCoordinators: updated });
  };

  const handleRoleChange = (alias: string, newRole: 'owner' | 'coordinator') => {
    if (newRole !== 'owner') return;
    publishKatalMetric(`${metricPrefix}_ownershipTransferred`, alias);
    const updatedCoordinators = additionalCoordinators.filter((a) => a !== alias);
    if (ownerAlias && !updatedCoordinators.includes(ownerAlias)) {
      updatedCoordinators.push(ownerAlias);
    }

    // amazonContacts[0] must be the owner (backend invariant).
    const matchesNewOwner = (c: { email?: string; alias?: string }): boolean =>
      c.alias === alias || getAliasFromEmail(c.email) === alias;
    const existingContacts = currentFields.amazonContacts ?? [];
    const newOwnerContact = existingContacts.find(matchesNewOwner);
    const reorderedContacts = newOwnerContact
      ? [
          newOwnerContact,
          ...existingContacts.filter((c) => !matchesNewOwner(c)).slice(0, MAX_AMAZON_CONTACTS - 1),
        ]
      : undefined;

    updateFieldHandler({
      ...currentFields,
      ownerAlias: alias,
      additionalCoordinators: updatedCoordinators,
      ...(reorderedContacts && { amazonContacts: reorderedContacts }),
    });
    setEditingRole(null);
  };

  const handlePartnerChange = (option: OptionDefinition) => {
    const partnerValue = option.value as ExternalPartner;
    publishKatalMetric(`${metricPrefix}_partnerSelected`, partnerValue);
    if (partnerValue !== ExternalPartner.OTHER) setOtherPartnerDraft(null);
    updateFieldHandler({
      ...currentFields,
      externalPlanningPartner: partnerValue,
      ...(option.value !== ExternalPartner.OTHER && { otherPartner: undefined }),
    });
  };

  const roleColumnDefinition = {
    id: 'role',
    header: t('teamPartnershipStep.roleHeader'),
    cell: (item: CoordinatorRow) => {
      if (item.role === 'owner') {
        return <Badge color="green">{t('teamPartnershipStep.roleOwner')}</Badge>;
      }
      if (editingRole === item.alias && canTransferOwnership) {
        return (
          <SpaceBetween direction="horizontal" size="xs">
            <Select
              selectedOption={{
                value: 'coordinator',
                label: t('teamPartnershipStep.roleCoordinator'),
              }}
              options={[
                { value: 'coordinator', label: t('teamPartnershipStep.roleCoordinator') },
                {
                  value: 'owner',
                  label: t('teamPartnershipStep.roleOwner'),
                  description: t('teamPartnershipStep.ownerOptionDescription'),
                },
              ]}
              onChange={({ detail }) => {
                if (detail.selectedOption.value === 'owner') {
                  setTransferTarget(item.alias);
                } else {
                  setEditingRole(null);
                }
              }}
              ariaLabel={t('teamPartnershipStep.editRole')}
              expandToViewport
            />
            <Button
              variant="icon"
              iconName="close"
              onClick={() => setEditingRole(null)}
              ariaLabel={t('teamPartnershipStep.cancelRoleEdit')}
            />
          </SpaceBetween>
        );
      }
      return (
        <SpaceBetween direction="horizontal" size="xs" alignItems="center">
          <Badge color="grey">{t('teamPartnershipStep.roleCoordinator')}</Badge>
          {canTransferOwnership && (
            <Button
              variant="inline-icon"
              iconName="edit"
              onClick={() => setEditingRole(item.alias)}
              ariaLabel={t('teamPartnershipStep.editRole')}
            />
          )}
        </SpaceBetween>
      );
    },
  };

  return (
    <Container
      header={<Header variant="h2">{t('teamPartnershipStep.coordinatorsPartners')}</Header>}
    >
      <SpaceBetween size="l">
        <FormField
          description={t('teamPartnershipStep.coordinatorsDescription')}
          label={<OptionalLabel>{t('teamPartnershipStep.coordinatorsLabel')}</OptionalLabel>}
          errorText={errors?.coordinatorSearch}
        >
          <SpaceBetween size="s">
            {canEdit && (
              <FormField
                constraintText={t('teamPartnershipStep.coordinatorsConstraint', {
                  max: MAX_COORDINATORS,
                  current: additionalCoordinators.length,
                })}
              >
                <Autosuggest
                  filteringType="manual"
                  options={coordinatorSuggestions}
                  statusType={papiSearch.loading ? 'loading' : undefined}
                  placeholder={t('teamPartnershipStep.coordinatorsPlaceholder')}
                  value={coordinatorSearch}
                  onChange={({ detail }) => {
                    setCoordinatorSearch(detail.value);
                    papiSearch.search(detail.value);
                  }}
                  onSelect={({ detail }) => handleAddCoordinator(detail.value)}
                  disabled={additionalCoordinators.length >= MAX_COORDINATORS}
                />
              </FormField>
            )}
            <Table
              ariaLabels={{ tableLabel: t('teamPartnershipStep.coordinatorsLabel') }}
              trackBy="alias"
              items={rows}
              columnDefinitions={[
                {
                  id: 'name',
                  header: t('teamPartnershipStep.nameHeader'),
                  cell: (item) => item.name || item.alias,
                },
                {
                  id: 'alias',
                  header: t('teamPartnershipStep.aliasHeader'),
                  cell: (item) => `@${item.alias}`,
                },
                roleColumnDefinition,
                {
                  id: 'action',
                  header: t('teamPartnershipStep.actionHeader'),
                  cell: (item) => (
                    <Button
                      variant="link"
                      disabled={item.role === 'owner' || !canEdit}
                      disabledReason={
                        item.role === 'owner'
                          ? t('teamPartnershipStep.removeOwnerPopover')
                          : undefined
                      }
                      onClick={() => handleRemoveCoordinator(item.alias)}
                    >
                      {t('teamPartnershipStep.remove')}
                    </Button>
                  ),
                },
              ]}
              variant="embedded"
              empty={null}
            />
          </SpaceBetween>
        </FormField>

        <FormField
          description={t('teamPartnershipStep.partnerDescription')}
          label={<OptionalLabel>{t('teamPartnershipStep.partnerLabel')}</OptionalLabel>}
          errorText={errors?.externalPartner}
        >
          <SpaceBetween size="xs">
            <Select
              options={partnerOptions}
              placeholder={t('teamPartnershipStep.partnerPlaceholder')}
              selectedOption={selectedPartner}
              onChange={({ detail }) => handlePartnerChange(detail.selectedOption)}
              readOnly={isReadOnly}
            />
          </SpaceBetween>
        </FormField>

        {currentFields.externalPlanningPartner === ExternalPartner.OTHER && (
          <FormField
            constraintText={t('error.inputTooLong', { maxLength: FIELD_LIMITS.OTHER_PARTNER_NAME })}
            description={t('teamPartnershipStep.otherPartnerDescription')}
            label={t('teamPartnershipStep.otherPartnerLabel')}
            errorText={errors?.otherPartnerName}
          >
            <Input
              placeholder={t('teamPartnershipStep.otherPartnerPlaceholder')}
              value={otherPartnerValue}
              onChange={({ detail }) =>
                setOtherPartnerDraft(detail.value.slice(0, FIELD_LIMITS.OTHER_PARTNER_NAME))
              }
              onBlur={() => {
                if (otherPartnerDraft !== null) {
                  updateFieldHandler({ ...currentFields, otherPartner: otherPartnerDraft.trim() });
                  setOtherPartnerDraft(null);
                }
              }}
              readOnly={isReadOnly}
            />
          </FormField>
        )}

        <FormField
          description={t('teamPartnershipStep.elgDescription')}
          label={<OptionalLabel>{t('teamPartnershipStep.elgLabel')}</OptionalLabel>}
          errorText={errors?.elg}
        >
          <Multiselect
            options={elgOptions}
            placeholder={t('teamPartnershipStep.elgPlaceholder')}
            selectedOptions={selectedElgs}
            onChange={({ detail }) =>
              updateFieldHandler({
                ...currentFields,
                elgAffiliations: detail.selectedOptions
                  .map((opt) => opt.value)
                  .filter((v): v is string => !!v),
              })
            }
            readOnly={isReadOnly}
          />
        </FormField>
      </SpaceBetween>
      <Modal
        visible={!!transferTarget}
        onDismiss={() => setTransferTarget(null)}
        header={t('teamPartnershipStep.transferOwnershipHeader')}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setTransferTarget(null)}>
                {t('teamPartnershipStep.cancelEdit')}
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  const target = transferTarget;
                  setTransferTarget(null);
                  if (target) handleRoleChange(target, 'owner');
                }}
              >
                {t('teamPartnershipStep.confirmTransfer')}
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        {t('teamPartnershipStep.transferOwnershipBody', { alias: transferTarget ?? '' })}
      </Modal>
    </Container>
  );
}
